import React from 'react';
import {
    Clock, Trophy, Flame,
    Zap, Star, TrendingUp, Calendar, Swords, Award, Target,
    Loader2, ChevronDown, ChevronUp
} from 'lucide-react';
import { useI18n } from '../i18n';
import { formatMemberText } from '../locales/member';

function formatPercent(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '–';
    return `${n.toFixed(2)}%`;
}

const socialSessionParams = (row) => ({
    sessionIds: (row.evidenceSessionIds || []).join(',')
});

const SessionDetails = ({
    t,
    sessions,
    loading,
    formatDate,
    formatDuration,
    showRank = false,
    showPoints = false,
    onSessionClick
}) => {
    if (loading) {
        return (
            <div className="session-details loading">
                <Loader2 size={16} className="spin" />
                <span>{t.statisticsAnalytics.loadingSessions}</span>
            </div>
        );
    }

    if (!sessions || sessions.length === 0) {
        return (
            <div className="session-details empty">
                <span>{t.statisticsAnalytics.noSessionData}</span>
            </div>
        );
    }

    const clickable = typeof onSessionClick === 'function';

    return (
        <div className="session-details">
            {clickable && (
                <p className="session-details-hint">{t.statisticsAnalytics.clickGameHint}</p>
            )}
            <div className="session-list">
                {sessions.map((session, idx) => {
                    const sid = session.sessionId;
                    const canOpen = clickable && sid != null;
                    return (
                        <div
                            key={sid || idx}
                            className={`session-item${canOpen ? ' session-item-clickable' : ''}`}
                            onClick={
                                canOpen
                                    ? (e) => {
                                          e.stopPropagation();
                                          onSessionClick(sid);
                                      }
                                    : undefined
                            }
                            role={canOpen ? 'button' : undefined}
                            tabIndex={canOpen ? 0 : undefined}
                            onKeyDown={
                                canOpen
                                    ? (e) => {
                                          if (e.key === 'Enter' || e.key === ' ') {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              onSessionClick(sid);
                                          }
                                      }
                                    : undefined
                            }
                        >
                            <span className="session-date">{formatDate(session.date)}</span>
                            <span className="session-game">{session.gameName}</span>
                            {session.durationMinutes > 0 && (
                                <span className="session-duration">{formatDuration(session.durationMinutes)}</span>
                            )}
                            {showRank && session.rank && (
                                <span className={`session-rank rank-${session.rank}`}>
                                    #{session.rank}
                                </span>
                            )}
                            {showPoints && (session.leaguePoints !== null && session.leaguePoints !== undefined) && (
                                <span className="session-points">
                                    {session.leaguePoints} MP
                                </span>
                            )}
                            {session.winner && (
                                <span className="session-winner">🏆 {session.winner}</span>
                            )}
                            {session.players && (
                                <span className="session-players">{session.players}</span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const ExpandablePlayerRows = ({
    t,
    players,
    rowKeyPrefix,
    detailType,
    renderStats,
    toggleRowExpansion,
    expandedRows,
    rowSessions,
    loadingRow,
    formatDate,
    formatDuration,
    onSessionClick,
    showRank = true,
    showPoints = false,
    extraParamsFromPlayer,
    rowIdFromPlayer
}) => (players || []).map((player, idx) => {
    const rowKey = `${rowKeyPrefix}-${rowIdFromPlayer ? rowIdFromPlayer(player) : player.playerId}`;
    const isRowExpanded = expandedRows?.[rowKey];
    const sessions = rowSessions?.[rowKey];
    return (
        <React.Fragment key={rowKey}>
            <div
                className={`table-row clickable ${isRowExpanded ? 'expanded' : ''}`}
                onClick={() => toggleRowExpansion(
                    rowKey,
                    detailType,
                    null,
                    player.playerId,
                    extraParamsFromPlayer?.(player)
                )}
            >
                <span className="col-rank rank-badge">{idx + 1}</span>
                <span className="col-game">
                    {player.playerName}
                    <ChevronDown size={14} className={`expand-icon ${isRowExpanded ? 'rotated' : ''}`} />
                </span>
                {renderStats(player)}
            </div>
            {isRowExpanded && (
                <SessionDetails
                    t={t}
                    sessions={sessions}
                    loading={loadingRow === rowKey}
                    formatDate={formatDate}
                    formatDuration={formatDuration}
                    showRank={showRank}
                    showPoints={showPoints}
                    onSessionClick={onSessionClick}
                />
            )}
        </React.Fragment>
    );
});

const StatSection = ({ title, subtitle, icon, children, isExpanded, onToggle }) => (
    <div className="stat-section">
        <div className="stat-section-header" onClick={onToggle}>
            <div className="stat-section-title">
                {icon}
                <div>
                    <h3>{title}</h3>
                    <small>{subtitle}</small>
                </div>
            </div>
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        {isExpanded && (
            <div className="stat-section-content">
                {children}
            </div>
        )}
    </div>
);

/**
 * Renders analytics blocks in admin-configured order (visibility + order).
 */
export default function StatisticsAnalyticsSections({
    t: tProp,
    stats,
    orderedSections,
    expandedSections,
    toggleSection,
    expandedRows,
    rowSessions,
    loadingRow,
    toggleRowExpansion,
    formatDate,
    formatDuration,
    onSessionClick
}) {
    const { t: tHook } = useI18n();
    const t = tProp || tHook;
    const expanded = (id) => expandedSections[id] === true;

    const renderSection = (sectionId) => {
        if (!stats) return null;

        switch (sectionId) {
            case 'longestGames':
                return (
                    <StatSection
                        title={t.statisticsAnalytics.longestGames}
                        subtitle={t.statisticsAnalytics.longestGamesSub}
                        icon={<Clock size={20} />}
                        isExpanded={expanded('longestGames')}
                        onToggle={() => toggleSection('longestGames')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.statisticsAnalytics.colGame}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colTotal}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colCount}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colAverage}</span>
                            </div>
                            {stats?.longestGames?.map((game, idx) => {
                                const rowKey = `longest-${game.gameId}`;
                                const isRowExpanded = expandedRows[rowKey];
                                const sessions = rowSessions[rowKey];
                                return (
                                    <React.Fragment key={game.gameId}>
                                        <div
                                            className={`table-row clickable ${isRowExpanded ? 'expanded' : ''}`}
                                            onClick={() => toggleRowExpansion(rowKey, 'longestGame', game.gameId)}
                                        >
                                            <span className="col-rank rank-badge">{idx + 1}</span>
                                            <span className="col-game">
                                                {game.gameImage && (
                                                    <img src={game.gameImage} alt="" className="game-thumb" />
                                                )}
                                                {game.gameName}
                                                <ChevronDown size={14} className={`expand-icon ${isRowExpanded ? 'rotated' : ''}`} />
                                            </span>
                                            <span className="col-stat highlight">{formatDuration(game.totalMinutes)}</span>
                                            <span className="col-stat">{game.sessionCount}x</span>
                                            <span className="col-stat">{formatDuration(Math.round(game.avgMinutes))}</span>
                                        </div>
                                        {isRowExpanded && (
                                            <SessionDetails
                                                t={t}
                                                sessions={sessions}
                                                loading={loadingRow === rowKey}
                                                formatDate={formatDate}
                                                formatDuration={formatDuration}
                                                onSessionClick={onSessionClick}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </StatSection>
                );

            case 'mostPlayed':
                return (
                    <StatSection
                        title={t.statisticsAnalytics.mostPlayed}
                        subtitle={t.statisticsAnalytics.mostPlayedSub}
                        icon={<Star size={20} />}
                        isExpanded={expanded('mostPlayed')}
                        onToggle={() => toggleSection('mostPlayed')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.statisticsAnalytics.colGame}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colPlayed}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colPlayers}</span>
                            </div>
                            {stats?.mostPlayedGames?.map((game, idx) => {
                                const rowKey = `played-${game.gameId}`;
                                const isRowExpanded = expandedRows[rowKey];
                                const sessions = rowSessions[rowKey];
                                return (
                                    <React.Fragment key={game.gameId}>
                                        <div
                                            className={`table-row clickable ${isRowExpanded ? 'expanded' : ''}`}
                                            onClick={() => toggleRowExpansion(rowKey, 'mostPlayed', game.gameId)}
                                        >
                                            <span className="col-rank rank-badge">{idx + 1}</span>
                                            <span className="col-game">
                                                {game.gameImage && (
                                                    <img src={game.gameImage} alt="" className="game-thumb" />
                                                )}
                                                {game.gameName}
                                                <ChevronDown size={14} className={`expand-icon ${isRowExpanded ? 'rotated' : ''}`} />
                                            </span>
                                            <span className="col-stat highlight">{game.playCount}x</span>
                                            <span className="col-stat">{game.uniquePlayers}</span>
                                        </div>
                                        {isRowExpanded && (
                                            <SessionDetails
                                                sessions={sessions}
                                                loading={loadingRow === rowKey}
                                                formatDate={formatDate}
                                                formatDuration={formatDuration}
                                                onSessionClick={onSessionClick}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </StatSection>
                );

            case 'winRates':
                return (
                    <StatSection
                        title={t.statisticsAnalytics.winRates}
                        subtitle={t.statisticsAnalytics.winRatesSub}
                        icon={<Trophy size={20} />}
                        isExpanded={expanded('winRates')}
                        onToggle={() => toggleSection('winRates')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.common.player}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colWinPct}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colWins}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colGames}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colAvgRank}</span>
                            </div>
                            {stats?.playerWinRates?.map((player, idx) => {
                                const rowKey = `winrate-${player.playerId}`;
                                const isRowExpanded = expandedRows[rowKey];
                                const sessions = rowSessions[rowKey];
                                return (
                                    <React.Fragment key={player.playerId}>
                                        <div
                                            className={`table-row clickable ${isRowExpanded ? 'expanded' : ''}`}
                                            onClick={() => toggleRowExpansion(rowKey, 'playerWins', null, player.playerId)}
                                        >
                                            <span className="col-rank rank-badge">{idx + 1}</span>
                                            <span className="col-game">
                                                {player.playerName}
                                                <ChevronDown size={14} className={`expand-icon ${isRowExpanded ? 'rotated' : ''}`} />
                                            </span>
                                            <span className="col-stat highlight">{formatPercent(player.winRate)}</span>
                                            <span className="col-stat">{player.wins}</span>
                                            <span className="col-stat">{player.gamesPlayed}</span>
                                            <span className="col-stat">{player.avgRank?.toFixed(1)}</span>
                                        </div>
                                        {isRowExpanded && (
                                            <SessionDetails
                                                sessions={sessions}
                                                loading={loadingRow === rowKey}
                                                formatDate={formatDate}
                                                formatDuration={formatDuration}
                                                showRank={true}
                                                onSessionClick={onSessionClick}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </StatSection>
                );

            case 'winStreaks':
                if (!stats?.winStreaks?.length) return null;
                return (
                    <StatSection
                        title={t.statisticsAnalytics.winStreaks}
                        subtitle={t.statisticsAnalytics.winStreaksSub}
                        icon={<Flame size={20} />}
                        isExpanded={expanded('winStreaks')}
                        onToggle={() => toggleSection('winStreaks')}
                    >
                        <div className="streak-cards">
                            {stats.winStreaks.map((streak, idx) => (
                                <div key={idx} className="streak-card">
                                    <div className="streak-flames">
                                        {Array.from({ length: Math.min(streak.streakLength, 5) }).map((_, i) => (
                                            <Flame key={i} size={16} className="flame" />
                                        ))}
                                    </div>
                                    <div className="streak-name">{streak.playerName}</div>
                                    <div className="streak-count">{formatMemberText(t.statisticsAnalytics.winStreakCount, { count: streak.streakLength })}</div>
                                    <div className="streak-dates">
                                        {formatDate(streak.streakStart)} - {formatDate(streak.streakEnd)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </StatSection>
                );

            case 'highestScores':
                if (!stats?.highestScores?.length) return null;
                return (
                    <StatSection
                        title={t.statisticsAnalytics.highestScores}
                        subtitle={t.statisticsAnalytics.highestScoresSub}
                        icon={<Zap size={20} />}
                        isExpanded={expanded('highestScores')}
                        onToggle={() => toggleSection('highestScores')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.common.player}</span>
                                <span className="col-game">{t.statisticsAnalytics.colGame}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colScore}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colDate}</span>
                            </div>
                            {stats.highestScores.map((score, idx) => (
                                <div key={idx} className="table-row">
                                    <span className="col-rank rank-badge">{idx + 1}</span>
                                    <span className="col-game">{score.playerName}</span>
                                    <span className="col-game">{score.gameName}</span>
                                    <span className="col-stat highlight">{score.score}</span>
                                    <span className="col-stat">{formatDate(score.date)}</span>
                                </div>
                            ))}
                        </div>
                    </StatSection>
                );

            case 'closestGames':
                if (!stats?.closestGames?.length) return null;
                return (
                    <StatSection
                        title={t.statisticsAnalytics.closestGames}
                        subtitle={t.statisticsAnalytics.closestGamesSub}
                        icon={<Swords size={20} />}
                        isExpanded={expanded('closestGames')}
                        onToggle={() => toggleSection('closestGames')}
                    >
                        <div className="stats-table compact">
                            {stats.closestGames.slice(0, 5).map((game, idx) => (
                                <div key={idx} className="table-row closest-row">
                                    <span className="game-info">
                                        <strong>{game.gameName}</strong>
                                        <small>{formatDate(game.date)}</small>
                                    </span>
                                    <span className="matchup">
                                        <span className="winner">{game.winnerName} ({game.winnerScore})</span>
                                        <span className="vs">{t.statisticsAnalytics.vs}</span>
                                        <span className="runnerup">{game.runnerupName} ({game.runnerupScore})</span>
                                    </span>
                                    <span className="diff">Δ {game.scoreDiff}</span>
                                </div>
                            ))}
                        </div>
                    </StatSection>
                );

            case 'playerFavorites':
                if (!stats?.playerFavorites?.length) return null;
                return (
                    <StatSection
                        title={t.statisticsAnalytics.playerFavorites}
                        subtitle={t.statisticsAnalytics.playerFavoritesSub}
                        icon={<Award size={20} />}
                        isExpanded={expanded('playerFavorites')}
                        onToggle={() => toggleSection('playerFavorites')}
                    >
                        <div className="favorites-grid">
                            {stats.playerFavorites.map((fav, idx) => (
                                <div key={idx} className="favorite-card">
                                    <div className="fav-player">{fav.playerName}</div>
                                    <div className="fav-game">
                                        {fav.gameImage && (
                                            <img src={fav.gameImage} alt="" className="fav-img" />
                                        )}
                                        <span>{fav.favoritGame}</span>
                                    </div>
                                    <div className="fav-stats">
                                        {formatMemberText(t.statisticsAnalytics.playedWins, { played: fav.playCount, wins: fav.winsInGame })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </StatSection>
                );

            case 'monthlyActivity':
                if (!stats?.monthlyActivity?.length) return null;
                return (
                    <StatSection
                        title={t.statisticsAnalytics.monthlyActivity}
                        subtitle={t.statisticsAnalytics.monthlyActivitySub}
                        icon={<Calendar size={20} />}
                        isExpanded={expanded('monthlyActivity')}
                        onToggle={() => toggleSection('monthlyActivity')}
                    >
                        <div className="activity-chart">
                            {stats.monthlyActivity.slice(0, 12).map((month, idx) => {
                                const maxSessions = Math.max(...stats.monthlyActivity.slice(0, 12).map((m) => m.sessionCount));
                                const height = maxSessions ? (month.sessionCount / maxSessions) * 100 : 0;
                                return (
                                    <div key={idx} className="bar-container">
                                        <div
                                            className="bar"
                                            style={{ height: `${height}%` }}
                                            title={formatMemberText(t.statisticsAnalytics.sessionsTooltip, { count: month.sessionCount })}
                                        >
                                            <span className="bar-value">{month.sessionCount}</span>
                                        </div>
                                        <span className="bar-label">{month.month}/{String(month.year).slice(2)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </StatSection>
                );

            case 'avgPointsPerGame':
                if (!stats?.avgPointsPerGame?.length) return null;
                return (
                    <StatSection
                        title={t.statisticsAnalytics.avgLeaguePoints}
                        subtitle={t.statisticsAnalytics.avgLeaguePointsSub}
                        icon={<Trophy size={20} />}
                        isExpanded={expanded('avgPointsPerGame')}
                        onToggle={() => toggleSection('avgPointsPerGame')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.common.player}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colAverage}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colTotal}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colGames}</span>
                            </div>
                            {stats.avgPointsPerGame.map((player, idx) => {
                                const rowKey = `avgpoints-${player.playerId}`;
                                const isRowExpanded = expandedRows[rowKey];
                                const sessions = rowSessions[rowKey];
                                return (
                                    <React.Fragment key={player.playerId}>
                                        <div
                                            className={`table-row clickable ${isRowExpanded ? 'expanded' : ''}`}
                                            onClick={() => toggleRowExpansion(rowKey, 'playerWins', null, player.playerId)}
                                        >
                                            <span className="col-rank rank-badge">{idx + 1}</span>
                                            <span className="col-game">
                                                {player.playerName}
                                                <ChevronDown size={14} className={`expand-icon ${isRowExpanded ? 'rotated' : ''}`} />
                                            </span>
                                            <span className="col-stat highlight">{parseFloat(player.avgPointsPerGame).toFixed(2)}</span>
                                            <span className="col-stat">{player.totalPoints}</span>
                                            <span className="col-stat">{player.gamesPlayed}</span>
                                        </div>
                                        {isRowExpanded && (
                                            <SessionDetails
                                                t={t}
                                                sessions={sessions}
                                                loading={loadingRow === rowKey}
                                                formatDate={formatDate}
                                                formatDuration={formatDuration}
                                                showRank={true}
                                                showPoints={true}
                                                onSessionClick={onSessionClick}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </StatSection>
                );

            case 'pointsLeaderboard':
                return (
                    <StatSection
                        title={t.statisticsAnalytics.leaguePointsTitle}
                        subtitle={t.statisticsAnalytics.leaguePointsSub}
                        icon={<Trophy size={20} />}
                        isExpanded={expanded('pointsLeaderboard')}
                        onToggle={() => toggleSection('pointsLeaderboard')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.common.player}</span>
                                <span className="col-stat">MP</span>
                                <span className="col-stat">{t.statisticsAnalytics.colGames}</span>
                            </div>
                            <ExpandablePlayerRows
                                t={t}
                                players={stats.pointsLeaderboard}
                                rowKeyPrefix="points"
                                detailType="playerWins"
                                showRank={true}
                                showPoints={true}
                                toggleRowExpansion={toggleRowExpansion}
                                expandedRows={expandedRows}
                                rowSessions={rowSessions}
                                loadingRow={loadingRow}
                                formatDate={formatDate}
                                formatDuration={formatDuration}
                                onSessionClick={onSessionClick}
                                renderStats={(player) => (
                                    <>
                                        <span className="col-stat highlight">{player.totalPoints}</span>
                                        <span className="col-stat">{player.gamesPlayed}</span>
                                    </>
                                )}
                            />
                        </div>
                    </StatSection>
                );

            case 'medalPercentage':
                return (
                    <StatSection
                        title={t.statisticsAnalytics.medalPercentage}
                        subtitle={t.statisticsAnalytics.medalPercentageSub}
                        icon={<Award size={20} />}
                        isExpanded={expanded('medalPercentage')}
                        onToggle={() => toggleSection('medalPercentage')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.common.player}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colMedalPct}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colMedals}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colGames}</span>
                            </div>
                            <ExpandablePlayerRows
                                t={t}
                                players={stats.medalPercentages}
                                rowKeyPrefix="medalpct"
                                detailType="playerMedals"
                                showRank={true}
                                showPoints={true}
                                toggleRowExpansion={toggleRowExpansion}
                                expandedRows={expandedRows}
                                rowSessions={rowSessions}
                                loadingRow={loadingRow}
                                formatDate={formatDate}
                                formatDuration={formatDuration}
                                onSessionClick={onSessionClick}
                                renderStats={(player) => (
                                    <>
                                        <span className="col-stat highlight">{formatPercent(player.medalPercentage)}</span>
                                        <span className="col-stat">{player.medalPlacements}</span>
                                        <span className="col-stat">{player.gamesPlayed}</span>
                                    </>
                                )}
                            />
                        </div>
                    </StatSection>
                );

            case 'medalTable':
                return (
                    <StatSection
                        title={t.statisticsAnalytics.medalTable}
                        subtitle={t.statisticsAnalytics.medalTableSub}
                        icon={<Award size={20} />}
                        isExpanded={expanded('medalTable')}
                        onToggle={() => toggleSection('medalTable')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.common.player}</span>
                                <span className="col-stat">🥇</span>
                                <span className="col-stat">🥈</span>
                                <span className="col-stat">🥉</span>
                                <span className="col-stat">{t.statisticsAnalytics.colTotalShort}</span>
                            </div>
                            <ExpandablePlayerRows
                                t={t}
                                players={stats.medalTable}
                                rowKeyPrefix="medals"
                                detailType="playerMedals"
                                showRank={true}
                                showPoints={true}
                                toggleRowExpansion={toggleRowExpansion}
                                expandedRows={expandedRows}
                                rowSessions={rowSessions}
                                loadingRow={loadingRow}
                                formatDate={formatDate}
                                formatDuration={formatDuration}
                                onSessionClick={onSessionClick}
                                renderStats={(player) => (
                                    <>
                                        <span className="col-stat highlight">{player.gold}</span>
                                        <span className="col-stat">{player.silver}</span>
                                        <span className="col-stat">{player.bronze}</span>
                                        <span className="col-stat">{player.totalMedals}</span>
                                    </>
                                )}
                            />
                        </div>
                    </StatSection>
                );

            case 'headToHead':
                if (!stats?.headToHead?.length) return null;
                return (
                    <StatSection
                        title={t.statisticsAnalytics.headToHead}
                        subtitle={t.statisticsAnalytics.headToHeadSub}
                        icon={<TrendingUp size={20} />}
                        isExpanded={expanded('headToHead')}
                        onToggle={() => toggleSection('headToHead')}
                    >
                        <div className="h2h-list">
                            {stats.headToHead.slice(0, 10).map((matchup, idx) => (
                                <div key={idx} className="h2h-row">
                                    <span className="h2h-winner">{matchup.winnerName}</span>
                                    <span className="h2h-score">{matchup.timesBeaten}x</span>
                                    <span className="h2h-loser">{matchup.loserName}</span>
                                </div>
                            ))}
                        </div>
                    </StatSection>
                );

            case 'socialNemesis':
                if (!stats?.socialNemesis?.length) return null;
                return (
                    <StatSection
                        title={t.statisticsAnalytics.socialNemesis}
                        subtitle={t.statisticsAnalytics.socialNemesisSub}
                        icon={<Swords size={20} />}
                        isExpanded={expanded('socialNemesis')}
                        onToggle={() => toggleSection('socialNemesis')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.common.player}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colCount}</span>
                                <span className="col-stat">{t.statisticsAnalytics.socialNemesis}</span>
                            </div>
                            <ExpandablePlayerRows
                                t={t}
                                players={stats.socialNemesis}
                                rowKeyPrefix="nemesis"
                                detailType="socialEvidence"
                                extraParamsFromPlayer={socialSessionParams}
                                showRank={true}
                                showPoints={true}
                                toggleRowExpansion={toggleRowExpansion}
                                expandedRows={expandedRows}
                                rowSessions={rowSessions}
                                loadingRow={loadingRow}
                                formatDate={formatDate}
                                formatDuration={formatDuration}
                                onSessionClick={onSessionClick}
                                renderStats={(row) => (
                                    <>
                                        <span className="col-stat highlight">{row.timesAbove}/{row.encounters}</span>
                                        <span className="col-stat">{row.nemesisName}</span>
                                    </>
                                )}
                            />
                        </div>
                    </StatSection>
                );

            case 'socialKingmaker':
                if (!stats?.socialKingmaker?.length) return null;
                return (
                    <StatSection
                        title={t.statisticsAnalytics.socialKingmaker}
                        subtitle={t.statisticsAnalytics.socialKingmakerSub}
                        icon={<Award size={20} />}
                        isExpanded={expanded('socialKingmaker')}
                        onToggle={() => toggleSection('socialKingmaker')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.common.player}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colWinPct}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colGames}</span>
                            </div>
                            <ExpandablePlayerRows
                                t={t}
                                players={stats.socialKingmaker}
                                rowKeyPrefix="kingmaker"
                                detailType="socialEvidence"
                                extraParamsFromPlayer={socialSessionParams}
                                showRank={true}
                                showPoints={true}
                                toggleRowExpansion={toggleRowExpansion}
                                expandedRows={expandedRows}
                                rowSessions={rowSessions}
                                loadingRow={loadingRow}
                                formatDate={formatDate}
                                formatDuration={formatDuration}
                                onSessionClick={onSessionClick}
                                renderStats={(row) => (
                                    <>
                                        <span className="col-stat highlight">{formatPercent(row.index)}</span>
                                        <span className="col-stat">{row.tightRunnerUp}/{row.sessions}</span>
                                    </>
                                )}
                            />
                        </div>
                    </StatSection>
                );

            case 'socialCloser':
                if (!stats?.socialCloser?.length) return null;
                return (
                    <StatSection
                        title={t.statisticsAnalytics.socialCloser}
                        subtitle={t.statisticsAnalytics.socialCloserSub}
                        icon={<Target size={20} />}
                        isExpanded={expanded('socialCloser')}
                        onToggle={() => toggleSection('socialCloser')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.common.player}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colWinPct}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colGames}</span>
                            </div>
                            <ExpandablePlayerRows
                                t={t}
                                players={stats.socialCloser}
                                rowKeyPrefix="closer"
                                detailType="socialEvidence"
                                extraParamsFromPlayer={socialSessionParams}
                                showRank={true}
                                showPoints={true}
                                toggleRowExpansion={toggleRowExpansion}
                                expandedRows={expandedRows}
                                rowSessions={rowSessions}
                                loadingRow={loadingRow}
                                formatDate={formatDate}
                                formatDuration={formatDuration}
                                onSessionClick={onSessionClick}
                                renderStats={(row) => (
                                    <>
                                        <span className="col-stat highlight">{formatPercent(row.index)}</span>
                                        <span className="col-stat">{row.closeWins}/{row.closeSessions}</span>
                                    </>
                                )}
                            />
                        </div>
                    </StatSection>
                );

            case 'socialChoker':
                if (!stats?.socialChoker?.length) return null;
                return (
                    <StatSection
                        title={t.statisticsAnalytics.socialChoker}
                        subtitle={t.statisticsAnalytics.socialChokerSub}
                        icon={<Zap size={20} />}
                        isExpanded={expanded('socialChoker')}
                        onToggle={() => toggleSection('socialChoker')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.common.player}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colWinPct}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colGames}</span>
                            </div>
                            <ExpandablePlayerRows
                                t={t}
                                players={stats.socialChoker}
                                rowKeyPrefix="choker"
                                detailType="socialEvidence"
                                extraParamsFromPlayer={socialSessionParams}
                                showRank={true}
                                showPoints={true}
                                toggleRowExpansion={toggleRowExpansion}
                                expandedRows={expandedRows}
                                rowSessions={rowSessions}
                                loadingRow={loadingRow}
                                formatDate={formatDate}
                                formatDuration={formatDuration}
                                onSessionClick={onSessionClick}
                                renderStats={(row) => (
                                    <>
                                        <span className="col-stat highlight">{formatPercent(row.index)}</span>
                                        <span className="col-stat">{row.tightNearMiss}/{row.topThree}</span>
                                    </>
                                )}
                            />
                        </div>
                    </StatSection>
                );

            case 'socialGiantSlayer':
                if (!stats?.socialGiantSlayer?.length) return null;
                return (
                    <StatSection
                        title={t.statisticsAnalytics.socialGiantSlayer}
                        subtitle={t.statisticsAnalytics.socialGiantSlayerSub}
                        icon={<Swords size={20} />}
                        isExpanded={expanded('socialGiantSlayer')}
                        onToggle={() => toggleSection('socialGiantSlayer')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.common.player}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colWinPct}</span>
                                <span className="col-stat">{t.common.player}</span>
                            </div>
                            <ExpandablePlayerRows
                                t={t}
                                players={stats.socialGiantSlayer}
                                rowKeyPrefix="slayer"
                                detailType="socialEvidence"
                                extraParamsFromPlayer={socialSessionParams}
                                showRank={true}
                                showPoints={true}
                                toggleRowExpansion={toggleRowExpansion}
                                expandedRows={expandedRows}
                                rowSessions={rowSessions}
                                loadingRow={loadingRow}
                                formatDate={formatDate}
                                formatDuration={formatDuration}
                                onSessionClick={onSessionClick}
                                renderStats={(row) => (
                                    <>
                                        <span className="col-stat highlight">{formatPercent(row.index)}</span>
                                        <span className="col-stat">{row.leaderName}</span>
                                    </>
                                )}
                            />
                        </div>
                    </StatSection>
                );

            case 'socialSpecialist':
                if (!stats?.socialSpecialist?.length) return null;
                return (
                    <StatSection
                        title={t.statisticsAnalytics.socialSpecialist}
                        subtitle={t.statisticsAnalytics.socialSpecialistSub}
                        icon={<Star size={20} />}
                        isExpanded={expanded('socialSpecialist')}
                        onToggle={() => toggleSection('socialSpecialist')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.common.player}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colWinPct}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colGame}</span>
                            </div>
                            <ExpandablePlayerRows
                                t={t}
                                players={stats.socialSpecialist}
                                rowKeyPrefix="specialist"
                                detailType="socialEvidence"
                                extraParamsFromPlayer={socialSessionParams}
                                showRank={true}
                                showPoints={true}
                                toggleRowExpansion={toggleRowExpansion}
                                expandedRows={expandedRows}
                                rowSessions={rowSessions}
                                loadingRow={loadingRow}
                                formatDate={formatDate}
                                formatDuration={formatDuration}
                                onSessionClick={onSessionClick}
                                renderStats={(row) => (
                                    <>
                                        <span className="col-stat highlight">{formatPercent(row.winRate)}</span>
                                        <span className="col-stat">{row.gameName}</span>
                                    </>
                                )}
                            />
                        </div>
                    </StatSection>
                );

            case 'socialRivalry':
                if (!stats?.socialRivalry?.length) return null;
                return (
                    <StatSection
                        title={t.statisticsAnalytics.socialRivalry}
                        subtitle={t.statisticsAnalytics.socialRivalrySub}
                        icon={<Flame size={20} />}
                        isExpanded={expanded('socialRivalry')}
                        onToggle={() => toggleSection('socialRivalry')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.common.player}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colScore}</span>
                                <span className="col-stat">{t.statisticsAnalytics.vs}</span>
                            </div>
                            <ExpandablePlayerRows
                                t={t}
                                players={stats.socialRivalry.map((row) => ({
                                    ...row,
                                    playerId: row.playerAId,
                                    playerName: row.playerAName
                                }))}
                                rowKeyPrefix="rivalry"
                                rowIdFromPlayer={(row) => `${row.playerAId}-${row.playerBId}`}
                                detailType="socialEvidence"
                                extraParamsFromPlayer={socialSessionParams}
                                showRank={true}
                                showPoints={true}
                                toggleRowExpansion={toggleRowExpansion}
                                expandedRows={expandedRows}
                                rowSessions={rowSessions}
                                loadingRow={loadingRow}
                                formatDate={formatDate}
                                formatDuration={formatDuration}
                                onSessionClick={onSessionClick}
                                renderStats={(row) => (
                                    <>
                                        <span className="col-stat highlight">{row.score}</span>
                                        <span className="col-stat">{row.playerBName}</span>
                                    </>
                                )}
                            />
                        </div>
                    </StatSection>
                );

            case 'socialSkill':
                if (!stats?.socialSkill?.length) return null;
                return (
                    <StatSection
                        title={t.statisticsAnalytics.socialSkill}
                        subtitle={t.statisticsAnalytics.socialSkillSub}
                        icon={<TrendingUp size={20} />}
                        isExpanded={expanded('socialSkill')}
                        onToggle={() => toggleSection('socialSkill')}
                    >
                        <div className="stats-table">
                            <div className="table-header">
                                <span className="col-rank">#</span>
                                <span className="col-game">{t.common.player}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colScore}</span>
                                <span className="col-stat">{t.statisticsAnalytics.colGames}</span>
                            </div>
                            <ExpandablePlayerRows
                                t={t}
                                players={stats.socialSkill}
                                rowKeyPrefix="skill"
                                detailType="socialEvidence"
                                extraParamsFromPlayer={socialSessionParams}
                                showRank={true}
                                showPoints={true}
                                toggleRowExpansion={toggleRowExpansion}
                                expandedRows={expandedRows}
                                rowSessions={rowSessions}
                                loadingRow={loadingRow}
                                formatDate={formatDate}
                                formatDuration={formatDuration}
                                onSessionClick={onSessionClick}
                                renderStats={(row) => (
                                    <>
                                        <span className="col-stat highlight">{Number(row.ordinal).toFixed(1)}</span>
                                        <span className="col-stat">{row.gamesPlayed}</span>
                                    </>
                                )}
                            />
                        </div>
                    </StatSection>
                );

            default:
                return null;
        }
    };

    return (
        <>
            {orderedSections.map((section) => (
                <React.Fragment key={section.id}>
                    {renderSection(section.id)}
                </React.Fragment>
            ))}
        </>
    );
}
